import { Link } from "react-router-dom";
import { useFetch } from "@/lib/api";
import { StudioBanner, STUDIO_URL } from "@/components/center/StudioBanner";
import { ACADEMY_URL } from "@/components/layout/CenterLayout";
import { FolderOpen, MessageCircle, User, ShoppingBag, Sparkles, Images, ArrowUpRight, HelpCircle, GraduationCap, Wand2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { greeting } from "@/lib/format";
import { SpendingHome } from "@/components/common/Spending";

const CARDS = [
  { to: "/ordini", title: "Ordini", text: "Vai al negozio riservato Skin Lovers e scopri i prodotti che non hai ancora provato.", icon: ShoppingBag, id: "orders" },
  { to: "/documenti", title: "Documenti", text: "Attestati, contratti, loghi e documenti del tuo centro, sempre a portata di mano.", icon: FolderOpen, id: "documents" },
  { to: "/assistenza", title: "Assistenza", text: "Scrivici per ordini, prodotti, protocolli o qualsiasi dubbio.", icon: MessageCircle, id: "support" },
  { to: "/contenuti-social", title: "Contenuti social", text: "Grafiche, video e caption pronti da pubblicare, ogni settimana.", icon: Images, id: "social" },
  { to: "/account", title: "Il mio account", text: "I dati del centro, il tuo team di specialist e la password.", icon: User, id: "account" },
  { to: "/guida", title: "Guida all'app", text: "Cosa puoi fare in ogni sezione e tutte le novità.", icon: HelpCircle, id: "guide" },
  { href: ACADEMY_URL, title: "Academy", text: "Corsi e formazione Skin Lovers Professional sulla piattaforma Academy.", icon: GraduationCap, id: "academy" },
  { href: STUDIO_URL, title: "Skin Lovers Studio", text: "Analisi AI di pelle e corpo, referti col tuo brand e storico clienti.", icon: Wand2, id: "studio" },
];
function Featured() {
  const { data } = useFetch("/shop/featured");
  const p = data?.product;
  if (!p) return null;
  return (
    <section data-testid="featured-product" className="group mt-14 sm:mt-20 lux-card border-gold/30 grid sm:grid-cols-[minmax(0,2fr)_3fr] overflow-hidden animate-fade-up shadow-[0_30px_80px_-30px_rgba(212,175,55,0.35)]" style={{ animationDelay: "150ms" }}>
      <div className="relative bg-white aspect-square sm:aspect-auto sm:min-h-[300px] overflow-hidden">
        {p.image && <img src={p.image} alt={p.title} className="w-full h-full object-cover transition-transform duration-1000 ease-out group-hover:scale-105" />}
        <span className="absolute top-4 left-4 rounded-full bg-noir/85 backdrop-blur-md border border-gold/40 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.2em] text-gold">In evidenza</span>
      </div>
      <div className="relative p-6 sm:p-10 flex flex-col">
        <div className="pointer-events-none absolute -top-20 -right-20 w-56 h-56 rounded-full bg-gold/10 blur-3xl" />
        <p className="eyebrow">Prodotto della settimana</p>
        <h2 data-testid="featured-product-title" className="text-3xl sm:text-4xl font-light mt-3 leading-tight">{p.title}</h2>
        {data.note && <p className="font-serif italic text-xl mt-3 text-gold-light">{data.note}</p>}
        {p.description && <p className="text-sm text-warm-text mt-4 leading-relaxed line-clamp-4">{p.description}</p>}
        <a href={p.url} target="_blank" rel="noreferrer" data-testid="featured-product-buy" className="btn-gold mt-8 self-start">
          Scoprilo nel negozio <ArrowUpRight className="w-4 h-4" />
        </a>
      </div>
    </section>
  );
}

const SOON = [
  { title: "Assistente Skin Lovers", icon: Sparkles },
];

export default function CenterHome() {
  const { user } = useAuth();
  const c = user.center;
  return (
    <div>
      <section className="relative animate-fade-up">
        <div className="pointer-events-none absolute -top-16 -left-10 w-72 h-72 rounded-full bg-gold/10 blur-3xl animate-float" />
        <p className="relative eyebrow mb-4">{c.client_type_name || "My Skin Lovers"}</p>
        <h1 data-testid="center-greeting" className="relative text-4xl sm:text-5xl lg:text-6xl font-light leading-[1.05]">
          {greeting()},<br /><em className="font-normal">{c.center_name}</em>
        </h1>
        <div className="relative gold-line w-28 mt-6" />
        <p className="relative mt-5 text-sm sm:text-base text-warm-text max-w-md leading-relaxed">Questa è la tua area riservata Skin Lovers. Tutto ciò che serve al tuo centro, in un unico luogo.</p>
        <SpendingHome />
      </section>

      <Featured />
      <StudioBanner />

      <section className="mt-14 sm:mt-20 grid gap-4 sm:gap-5 md:grid-cols-2 lg:grid-cols-3">
        {CARDS.map((card, i) => {
          const Tag = card.href ? "a" : Link;
          const linkProps = card.href ? { href: card.href, target: "_blank", rel: "noopener noreferrer" } : { to: card.to };
          return (
          <Tag key={card.id} {...linkProps} data-testid={`home-card-${card.id}`}
            className="lux-card-link group overflow-hidden p-6 sm:p-8 flex flex-col min-h-[180px] md:min-h-[240px] animate-fade-up" style={{ animationDelay: `${220 + i * 90}ms` }}>
            <div className="pointer-events-none absolute -bottom-16 -right-16 w-40 h-40 rounded-full bg-gold/0 blur-2xl transition-colors duration-700 group-hover:bg-gold/15" />
            <div className="relative flex items-start justify-between">
              <span className="w-12 h-12 rounded-full border border-gold/30 bg-gold/[0.08] flex items-center justify-center transition-[background-color,box-shadow] duration-500 group-hover:bg-gold/20 group-hover:shadow-[0_0_24px_-4px_rgba(212,175,55,0.6)]">
                <card.icon className="w-5 h-5 text-gold" strokeWidth={1.4} />
              </span>
              <ArrowUpRight className="w-4 h-4 text-warm-text transition-[color,transform] duration-300 group-hover:text-gold group-hover:translate-x-0.5 group-hover:-translate-y-0.5" strokeWidth={1.4} />
            </div>
            <h2 className="relative mt-auto pt-8 text-2xl sm:text-[28px] font-normal">{card.title}</h2>
            <p className="relative mt-2 text-sm text-warm-text leading-relaxed">{card.text}</p>
          </Tag>
          );
        })}
      </section>

      <section className="mt-16 sm:mt-24">
        <div className="flex items-center gap-4 mb-6">
          <p className="eyebrow">Prossimamente</p>
          <span className="flex-1 hairline" />
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          {SOON.map((s, i) => (
            <div key={s.title} data-testid={`home-coming-soon-${i}`} className="rounded-2xl border border-dashed border-gold/25 bg-white/[0.02] px-5 py-5 flex items-center gap-4 text-warm-text">
              <s.icon className="w-5 h-5 text-gold animate-pulse" strokeWidth={1.3} />
              <div>
                <p className="font-serif text-lg text-ink/70">{s.title}</p>
                <p className="text-[11px] uppercase tracking-[0.16em]">In arrivo</p>
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
